import React from "react";
import { Link, useLocation } from "react-router-dom";
import { Leaf, Flame, LogOut, Shield } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { useI18n } from "@/i18n";
import { Button } from "@/components/ui/button";
import LanguageSwitcher from "@/components/LanguageSwitcher";

export default function Header() {
  const { user, login, logout, progress } = useApp();
  const { t } = useI18n();
  const location = useLocation();
  const streak = progress?.current_streak || 0;
  const approved = user && (user.is_admin || user.access_status === "approved");

  const NAV = [
    { to: "/learn", label: t("nav.courses"), testid: "nav-link-lessons", key: "lessons" },
    { to: "/flashcards", label: t("nav.vocab"), testid: "nav-link-flashcards", key: "flashcards" },
    { to: "/dictionary", label: t("nav.dict"), testid: "nav-link-dictionary", key: "dictionary" },
    { to: "/practice", label: t("nav.practice"), testid: "nav-link-practice", key: "practice" },
    { to: "/grammar", label: t("nav.grammar"), testid: "nav-link-grammar", key: "grammar" },
    { to: "/culture", label: t("nav.culture"), testid: "nav-link-culture", key: "culture" },
    { to: "/dashboard", label: t("nav.progress"), testid: "nav-link-dashboard", key: "dashboard" },
  ];

  const linkActive = (to) => (to === "/" ? location.pathname === "/" : location.pathname.startsWith(to));

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#F9F8F5]/85 border-b border-[#E2DFD7]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="brand-logo">
          <span className="w-9 h-9 rounded-xl bg-[#0E382B] flex items-center justify-center">
            <Leaf className="w-5 h-5 text-[#E8F0EC]" strokeWidth={2} />
          </span>
          <span className="font-serif text-xl font-semibold text-[#0E382B] tracking-tight">SuomiStep</span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {NAV.map((n) => (
            <Link
              key={n.key}
              to={n.to}
              data-testid={n.testid}
              className={`px-3.5 py-2 rounded-lg text-sm font-medium transition-colors ${
                linkActive(n.to) ? "bg-[#E8F0EC] text-[#0E382B]" : "text-[#52635B] hover:text-[#0E382B] hover:bg-[#F1EFEB]"
              }`}
            >
              {n.label}
            </Link>
          ))}
          {user?.is_admin && (
            <Link
              to="/admin"
              data-testid="nav-link-admin"
              className={`px-3.5 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 ${
                linkActive("/admin") ? "bg-[#EDF2FE] text-[#1E429F]" : "text-[#1E429F] hover:bg-[#EDF2FE]"
              }`}
            >
              <Shield className="w-4 h-4" /> {t("nav.admin")}
            </Link>
          )}
        </nav>

        <div className="flex items-center gap-2">
          <LanguageSwitcher />
          {user && approved && (
            <span
              className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#FEF3C7] border border-[#FCD34D] text-[#B45309] text-sm font-semibold"
              data-testid="nav-streak-badge"
            >
              <Flame className="w-4 h-4" />
              {streak}
            </span>
          )}
          {user ? (
            <div className="flex items-center gap-2">
              {user.picture ? (
                <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full border border-[#E2DFD7]" />
              ) : (
                <span className="w-8 h-8 rounded-full bg-[#1E429F] text-white flex items-center justify-center text-sm font-semibold">
                  {(user.name || "?").charAt(0)}
                </span>
              )}
              <button
                onClick={logout}
                data-testid="nav-auth-button"
                className="p-2 rounded-lg text-[#52635B] hover:bg-[#F1EFEB] hover:text-[#9B1C1C] transition-colors"
                title={t("nav.logout")}
              >
                <LogOut className="w-4.5 h-4.5" />
              </button>
            </div>
          ) : (
            <Button onClick={login} data-testid="nav-auth-button" className="rounded-full bg-[#0E382B] hover:bg-[#1C5442] text-white px-5">
              {t("nav.login")}
            </Button>
          )}
        </div>
      </div>

      <nav className="md:hidden flex items-center gap-1 overflow-x-auto px-4 pb-2.5 border-t border-[#E2DFD7]/60">
        {NAV.map((n) => (
          <Link
            key={n.key}
            to={n.to}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
              linkActive(n.to) ? "bg-[#E8F0EC] text-[#0E382B]" : "text-[#52635B]"
            }`}
          >
            {n.label}
          </Link>
        ))}
        {user?.is_admin && (
          <Link to="/admin" className="px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap text-[#1E429F]">
            {t("nav.admin")}
          </Link>
        )}
      </nav>
    </header>
  );
}
